// File: assets/src/js/features/contact/components/ContactContainer.tsx

import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ThemeVariables } from "../../../types";
import Skeleton from "react-loading-skeleton";

const ContactFormSkeleton: React.FC = () => {
  return (
    <div className="contact-form-wrapper">
      <p className="contact-intro">
        <Skeleton count={2} />
      </p>
      <div className="contact-form">
        {[...Array(5)].map((_, index) => (
          <div className="form-field" key={index}>
            <Skeleton
              width="30%"
              height={16}
              style={{ marginBottom: "0.4rem" }}
            />
            <Skeleton height={44} />
          </div>
        ))}
        <Skeleton height={48} style={{ marginTop: "0.75rem" }} />
      </div>
    </div>
  );
};

interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  website: string; // Honeypot field
}

interface SubmissionStatus {
  type: "success" | "error" | null;
  message: string | null;
}

const ContactContainer: React.FC = () => {
  const { t, ready: translationsReady } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    defaultValues: {
      website: "", // Initialize honeypot field
    },
  });
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>({
    type: null,
    message: null,
  });

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    setSubmissionStatus({ type: null, message: null });

    const themeVars = window.themeVariables as ThemeVariables | undefined;
    const ajaxUrl = themeVars?.ajaxUrl;
    const nonce = themeVars?.wpRestApiNonce;

    if (!ajaxUrl || !nonce) {
      console.error("AJAX URL or Nonce not defined in themeVariables.");
      setSubmissionStatus({
        type: "error",
        message: t("contactForm.error.config"),
      });
      return;
    }

    const formData = new URLSearchParams();
    formData.append("action", "contact_form_submit");
    formData.append("nonce", nonce);
    formData.append("name", data.name);
    formData.append("email", data.email);
    formData.append("phone", data.phone || "");
    formData.append("subject", data.subject);
    formData.append("message", data.message);
    formData.append("website", data.website); // Send honeypot field

    try {
      const response = await fetch(ajaxUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      const result = await response.json();
      const messageKey = result.data?.message; // Expect a key from the backend

      if (result.success) {
        setSubmissionStatus({
          type: "success",
          message: t(messageKey || "contactForm.success.messageSent"),
        });
        reset();
      } else {
        setSubmissionStatus({
          type: "error",
          message: t(messageKey || "contactForm.error.generic", {
            defaultValue: messageKey || t("contactForm.error.generic"),
          }),
        });
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      setSubmissionStatus({
        type: "error",
        message: t("contactForm.error.network"),
      });
    }
  };

  if (!translationsReady) {
    return <ContactFormSkeleton />;
  }

  return (
    <div className="contact-form-wrapper">
      <p className="contact-intro">{t("contactForm.intro")}</p>

      {submissionStatus.message && (
        <div
          className={`submission-message ${
            submissionStatus.type === "success"
              ? "success-message"
              : "error-message"
          }`}
          role="alert"
        >
          {submissionStatus.message}
        </div>
      )}

      <div className="form-container-relative">
        {isSubmitting && (
          <div className="form-submission-overlay">
            <div
              className="form-submission-spinner"
              aria-label={t("common.loading", "Loading...")}
            ></div>
          </div>
        )}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="contact-form"
          noValidate
        >
          <div
            className="form-field honeypot-field"
            style={{ display: "none" }}
            aria-hidden="true"
          >
            <label htmlFor="contact-website">
              {t("contactForm.website.label", "Website")}
            </label>
            <input
              id="contact-website"
              type="text"
              {...register("website")}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="form-field">
            <label htmlFor="contact-name">{t("contactForm.name.label")}</label>
            <input
              id="contact-name"
              type="text"
              {...register("name", {
                required: t("contactForm.name.required") as string,
              })}
              aria-invalid={errors.name ? "true" : "false"}
              disabled={isSubmitting}
            />
            {errors.name && (
              <span className="error-text">{errors.name.message}</span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="contact-email">
              {t("contactForm.email.label")}
            </label>
            <input
              id="contact-email"
              type="email"
              {...register("email", {
                required: t("contactForm.email.required") as string,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t("contactForm.email.invalid") as string,
                },
              })}
              aria-invalid={errors.email ? "true" : "false"}
              disabled={isSubmitting}
            />
            {errors.email && (
              <span className="error-text">{errors.email.message}</span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="contact-phone">
              {t("contactForm.phone.label")} ({t("common.optional")})
            </label>
            <input
              id="contact-phone"
              type="tel"
              {...register("phone")}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-field">
            <label htmlFor="contact-subject">
              {t("contactForm.subject.label")}
            </label>
            <input
              id="contact-subject"
              type="text"
              {...register("subject", {
                required: t("contactForm.subject.required") as string,
              })}
              aria-invalid={errors.subject ? "true" : "false"}
              disabled={isSubmitting}
            />
            {errors.subject && (
              <span className="error-text">{errors.subject.message}</span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="contact-message">
              {t("contactForm.message.label")}
            </label>
            <textarea
              id="contact-message"
              rows={5}
              {...register("message", {
                required: t("contactForm.message.required") as string,
              })}
              aria-invalid={errors.message ? "true" : "false"}
              disabled={isSubmitting}
            />
            {errors.message && (
              <span className="error-text">{errors.message.message}</span>
            )}
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("contactForm.submitting")
              : t("contactForm.submitButton")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactContainer;
